import { ApolloError } from '@apollo/client';
import { Box } from '@mui/material';

import {
  IPaginationProps,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LoadedContent,
  Typography,
  TableContainer,
} from '@occc-app/design-system';
import { RetailerNetworkUserFragment } from '@occc/graphql';

import { RetailerNetworkLocationGroupUsersTableRow } from './retailer-network-location-group-users-table-row.component';

type RetailerNetworkLocationGroupUsersTableProps = {
  data?: RetailerNetworkUserFragment[];
  loading: boolean;
  error?: ApolloError;
  isSearching?: boolean;
  isPhoneNumberVisible?: boolean;
  paginationProps: IPaginationProps;
  handleRedirectToUserDetailsPage: (userUuid: string) => void;
  handleRemoveUserFromLocationGroup: (userUuid: string) => void;
  handleRemoveUserFromRetailerNetwork: (openCommerceUserUuid: string) => void;
};

export const RetailerNetworkLocationGroupUsersTable = ({
  data,
  loading,
  error,
  isSearching,
  isPhoneNumberVisible = false,
  paginationProps,
  handleRedirectToUserDetailsPage,
  handleRemoveUserFromLocationGroup,
  handleRemoveUserFromRetailerNetwork,
}: RetailerNetworkLocationGroupUsersTableProps) => {
  const renderData = (items: RetailerNetworkUserFragment[]) => {
    const rows = items.map((item) => (
      <RetailerNetworkLocationGroupUsersTableRow
        key={item.uuid}
        item={item}
        isPhoneNumberVisible={isPhoneNumberVisible}
        handleRedirectToUserDetailsPage={handleRedirectToUserDetailsPage}
        handleRemoveUserFromLocationGroup={handleRemoveUserFromLocationGroup}
        handleRemoveUserFromRetailerNetwork={handleRemoveUserFromRetailerNetwork}
      />
    ));

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              {isPhoneNumberVisible && <TableCell>Mobile Phone Number</TableCell>}
              <TableCell>Role</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </TableContainer>
    );
  };

  const renderNoData = () => (
    <Box
      py={14}
      maxWidth={500}
      margin='0 auto'
      display='flex'
      alignItems='center'
      flexDirection='column'
    >
      <Typography variant='h6' align='center' paragraph>
        {isSearching ? 'No Results Found' : 'Add or Create Network Users and Admins'}
      </Typography>
      <Typography align='center' color='textSecondary' paragraph>
        {isSearching
          ? 'Please adjust your query and try again.'
          : 'Network admins can edit all the network details, add new sites and new users. Network users have read-only access.'}
      </Typography>
    </Box>
  );

  return (
    <LoadedContent<RetailerNetworkUserFragment>
      error={error}
      loading={loading}
      items={data}
      renderData={renderData}
      renderNoData={renderNoData}
      renderEmpty={renderNoData}
    />
  );
};
